import { BASE_URL } from 'src/common/config'
import { TokenService } from 'src/common/token-service'
import emitter from 'src/services/event-emitter'
import { connectToWs } from 'src/services/websocket-service'

let eventSource = null
let reconnectAttempts = 0
let sseFailureCount = 0
const MAX_FAILURES_BEFORE_FALLBACK = 5
const FAILURE_RESET_TIME = 60000
const MAX_RECONNECT_ATTEMPTS = 10
const FIXED_DELAY = 20000
let failureResetTimer = null

const tokenService = new TokenService()

export function connectToSSE() {
  if (eventSource) return

  const url = process.env.SSE_DOMAIN || `https://${location.hostname}${BASE_URL}/api/g/sse/`
  const queryString = buildQueryParams(tokenService.getToken())

  eventSource = new EventSource(`${url}?${queryString}`, { withCredentials: false })

  eventSource.onopen = () => {
    // eslint-disable-next-line no-console
    console.log('[CAS] ✅ SSE connected')
    reconnectAttempts = 0
    sseFailureCount = 0

    if (failureResetTimer) {
      clearTimeout(failureResetTimer)
      failureResetTimer = null
    }
  }

  eventSource.onmessage = (event) => {
    try {
      const data = JSON.parse(event.data)
      emitter.emit(data.message_type, data.content)
    } catch (error) {
      console.error('Error parsing SSE message:', error)
    }
  }

  eventSource.onerror = () => {
    console.warn(`⚠️ SSE error, attempt ${reconnectAttempts + 1}`)

    eventSource?.close()
    eventSource = null
    sseFailureCount++

    if (sseFailureCount >= MAX_FAILURES_BEFORE_FALLBACK) {
      console.error('❌ SSE unstable, switching to WebSocket...')
      connectToWs()
      return
    }

    if (!failureResetTimer) {
      failureResetTimer = setTimeout(() => {
        sseFailureCount = 0
      }, FAILURE_RESET_TIME)
    }

    let delay = reconnectAttempts < MAX_RECONNECT_ATTEMPTS ? (reconnectAttempts + 1) * 2000 : FIXED_DELAY
    setTimeout(connectToSSE, delay)
    reconnectAttempts++
  }
}

// 🔹 Restore SSE connection when the page becomes visible
document.addEventListener('visibilitychange', () => {
  if (document.visibilityState === 'visible') {
    if (!eventSource) {
      // eslint-disable-next-line no-console
      console.log('[CAS] 🔄 Restoring SSE connection after returning to the page...')
      connectToSSE()
    } else {
      connectWithCheckingState()
    }
  }
})

// 🔹 Reconnect when network is restored
window.addEventListener('online', () => {
  if (!eventSource) {
    // eslint-disable-next-line no-console
    console.log('[CAS] 🌍 Network restored, reconnecting SSE...')
    connectToSSE()
  } else {
    connectWithCheckingState()
  }
})

function buildQueryParams(token) {
  const params = new URLSearchParams()

  if (token) params.append('token', token)
  params.append('nocache', Date.now())

  return params.toString()
}

function connectWithCheckingState() {
  if (!eventSource || eventSource.readyState === EventSource.CLOSED) {
    connectToSSE()
  }
}
